import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Timeline,
  Typography,
  Tag,
  Collapse,
  Space,
  Spin,
  Empty,
  Button,
} from 'antd';
import {
  BugOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import http from 'utils/http';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

const SOURCE_COLOR = {
  dispatcher: 'blue',
  muxly: 'purple',
  ezcater: 'geekblue',
};

const EZCATER_STATUS_COLOR = {
  sent: 'success',
  failed: 'error',
  skipped: 'default',
  pending: 'warning',
};

function buildFallbackTimeline(logs, muxlyLogs, ezcaterEvents) {
  const timeline = [];

  (logs || []).forEach((log) => {
    timeline.push({ ...log, source: log.source || 'dispatcher' });
  });

  (muxlyLogs || []).forEach((log) => {
    timeline.push({ ...log, source: log.source || 'muxly' });
  });

  (ezcaterEvents || []).forEach((event) => {
    timeline.push({
      source: 'ezcater',
      timestamp: event.sent_at || event.created_at,
      direction: 'outbound',
      method: 'POST',
      endpoint: `ezcater/${event.ezcater_action || 'outbound'}`,
      event_type: event.ezcater_event_type || event.fulflld_event,
      request_data: event.request_payload,
      response_data: event.response_payload,
      status_code: null,
      error: event.error_message,
      status: event.status,
      id: event.id,
      fulflld_event: event.fulflld_event,
      fulflld_event_id: event.fulflld_event_id,
      ezcater_action: event.ezcater_action,
      delivery_id: event.delivery_id,
      created_at: event.created_at,
      sent_at: event.sent_at,
    });
  });

  timeline.sort((a, b) => {
    const ta = a.timestamp || '';
    const tb = b.timestamp || '';
    return tb.localeCompare(ta);
  });

  return timeline;
}

function DispatcherDebugPanel({ orderId, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [orderInfo, setOrderInfo] = useState(null);

  const fetchDispatcherLogs = async () => {
    setLoading(true);
    try {
      const response = await http.get(`v1/orders/${orderId}/dispatcher-logs/`);
      const data = response.data || {};
      setOrderInfo(data.order_info || null);

      if (Array.isArray(data.timeline)) {
        setTimeline(data.timeline);
      } else {
        setTimeline(
          buildFallbackTimeline(
            data.logs,
            data.muxly_logs,
            data.ezcater_outbound_events,
          ),
        );
      }
    } catch (error) {
      console.error('Failed to fetch dispatcher logs:', error);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && orderId) {
      fetchDispatcherLogs();
    }
  }, [open, orderId]);

  const getStatusIcon = (entry) => {
    if (entry.source === 'ezcater') {
      if (entry.status === 'sent') {
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      }
      if (entry.status === 'failed') {
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      }
      return <ClockCircleOutlined style={{ color: '#faad14' }} />;
    }

    const statusCode = entry.status_code;
    if (statusCode >= 200 && statusCode < 300) {
      return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
    }
    if (statusCode >= 400 || entry.error) {
      return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
    }
    return <ClockCircleOutlined style={{ color: '#faad14' }} />;
  };

  const getStatusColor = (entry) => {
    if (entry.source === 'ezcater') {
      return EZCATER_STATUS_COLOR[entry.status] || 'gray';
    }
    const statusCode = entry.status_code;
    if (statusCode >= 200 && statusCode < 300) return 'success';
    if (statusCode >= 400 || entry.error) return 'error';
    return 'warning';
  };

  const formatJson = (data) => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  const showMuxlySection = orderInfo?.is_muxly_order;
  const showEzcaterSection = orderInfo?.is_ezcater_order;

  return (
    <Drawer
      title={
        <Space>
          <BugOutlined />
          <span>Dispatcher Debug Panel</span>
          <Button
            type="text"
            size="small"
            icon={<ReloadOutlined />}
            onClick={fetchDispatcherLogs}
            loading={loading}
          >
            Refresh
          </Button>
        </Space>
      }
      placement="right"
      width={720}
      open={open}
      onClose={onClose}
    >
      {loading && !timeline.length ? (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          {orderInfo && (
            <div style={{ marginBottom: 24, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <Title level={5}>Order Information</Title>
              <Space direction="vertical" size="small">
                <Text>
                  <strong>Dispatcher:</strong> {orderInfo.dispatcher_name || 'None'}
                </Text>
                <Text>
                  <strong>Type:</strong>{' '}
                  <Tag color="blue">{orderInfo.dispatcher_type || 'N/A'}</Tag>
                </Text>
                <Text>
                  <strong>Trace ID:</strong>{' '}
                  <Tag color="purple">{orderInfo.dispatcher_trace_id || 'Not set'}</Tag>
                </Text>
                <Text>
                  <strong>Quote ID:</strong>{' '}
                  {orderInfo.dispatcher_quote_id || 'None'}
                </Text>
                <Text>
                  <strong>Delivery State:</strong>{' '}
                  <Tag color="processing">{orderInfo.delivery_state}</Tag>
                </Text>
                {orderInfo.driver_name && (
                  <Text>
                    <strong>Driver:</strong> {orderInfo.driver_name}
                  </Text>
                )}

                {showMuxlySection && (
                  <>
                    <Text>
                      <strong>Muxly order:</strong>{' '}
                      <Tag color="purple">Yes</Tag>
                    </Text>
                    <Text>
                      <strong>Muxly order reference:</strong>{' '}
                      {orderInfo.muxly_order_reference_id || 'None'}
                    </Text>
                    <Text>
                      <strong>Last Muxly status:</strong>{' '}
                      {orderInfo.muxly_last_external_partner_status || 'None'}
                    </Text>
                    <Text>
                      <strong>Last webhook event ID:</strong>{' '}
                      {orderInfo.muxly_last_webhook_event_id || 'None'}
                    </Text>
                    {orderInfo.muxly_request_id && (
                      <Text>
                        <strong>Muxly request ID:</strong>{' '}
                        {orderInfo.muxly_request_id}
                      </Text>
                    )}
                    {orderInfo.muxly_outbound_partner && (
                      <Text>
                        <strong>Outbound partner:</strong>{' '}
                        {orderInfo.muxly_outbound_partner}
                        {orderInfo.muxly_outbound_reference_id
                          ? ` (${orderInfo.muxly_outbound_reference_id})`
                          : ''}
                      </Text>
                    )}
                  </>
                )}

                {showEzcaterSection && (
                  <>
                    <Text>
                      <strong>ezCater order:</strong>{' '}
                      <Tag color="green">Yes</Tag>
                    </Text>
                    <Text>
                      <strong>ezCater delivery ID:</strong>{' '}
                      {orderInfo.ezcater_delivery_id || 'None'}
                    </Text>
                    <Text>
                      <strong>Courier assigned to ezCater:</strong>{' '}
                      {orderInfo.ezcater_courier_assigned ? 'Yes' : 'No'}
                    </Text>
                    <Text>
                      <strong>Last lifecycle event:</strong>{' '}
                      {orderInfo.ezcater_last_lifecycle_event || 'None'}
                    </Text>
                  </>
                )}
              </Space>
            </div>
          )}

          <Title level={5}>Integration Timeline</Title>
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            Dispatcher, Muxly, and ezCater events (newest first)
          </Text>

          {timeline.length > 0 ? (
            <Timeline>
              {timeline.map((entry, index) => {
                const source = entry.source || 'dispatcher';
                const label = entry.event_type || entry.endpoint || entry.ezcater_action || 'event';
                const statusLabel = entry.status_code != null
                  ? entry.status_code
                  : entry.status;

                return (
                  <Timeline.Item
                    key={entry.id || `${source}-${entry.timestamp}-${index}`}
                    dot={getStatusIcon(entry)}
                    color={getStatusColor(entry)}
                  >
                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                      <Space wrap>
                        <Tag color={SOURCE_COLOR[source] || 'default'}>{source}</Tag>
                        {entry.direction && (
                          <Tag>{entry.direction}</Tag>
                        )}
                        {entry.method && (
                          <Tag color="blue">{entry.method}</Tag>
                        )}
                        <Text strong>{label}</Text>
                        {statusLabel != null && statusLabel !== '' && (
                          <Tag color={getStatusColor(entry)}>{statusLabel}</Tag>
                        )}
                        {entry.duration_ms != null && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {entry.duration_ms}ms
                          </Text>
                        )}
                      </Space>

                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {entry.timestamp
                          ? dayjs(entry.timestamp).format('YYYY-MM-DD HH:mm:ss')
                          : '—'}
                        {entry.endpoint ? ` · ${entry.endpoint}` : ''}
                      </Text>

                      {entry.error && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          {entry.error}
                        </Text>
                      )}

                      <Collapse ghost size="small">
                        <Panel header="Request / Response" key="payload">
                          {entry.url && (
                            <Paragraph style={{ fontSize: 12 }}>
                              <strong>URL:</strong> {entry.url}
                            </Paragraph>
                          )}
                          {entry.request_data != null && (
                            <Paragraph style={{ fontSize: 12 }}>
                              <strong>Request:</strong>
                              <pre style={{
                                background: '#f0f0f0',
                                padding: 8,
                                borderRadius: 4,
                                overflow: 'auto',
                                maxHeight: 300,
                              }}
                              >
                                {formatJson(entry.request_data)}
                              </pre>
                            </Paragraph>
                          )}
                          {entry.response_data != null && (
                            <Paragraph style={{ fontSize: 12 }}>
                              <strong>Response:</strong>
                              <pre style={{
                                background: '#f0f0f0',
                                padding: 8,
                                borderRadius: 4,
                                overflow: 'auto',
                                maxHeight: 300,
                              }}
                              >
                                {formatJson(entry.response_data)}
                              </pre>
                            </Paragraph>
                          )}
                          {entry.status_code != null && (
                            <Paragraph style={{ fontSize: 12 }}>
                              <strong>Status:</strong>{' '}
                              {entry.status_code} {entry.status_text || ''}
                            </Paragraph>
                          )}
                          {source === 'ezcater' && (
                            <>
                              {entry.fulflld_event_id && (
                                <Paragraph style={{ fontSize: 12 }}>
                                  <strong>FULFLLD event ID:</strong>{' '}
                                  {entry.fulflld_event_id}
                                </Paragraph>
                              )}
                              {entry.delivery_id && (
                                <Paragraph style={{ fontSize: 12 }}>
                                  <strong>Delivery ID:</strong> {entry.delivery_id}
                                </Paragraph>
                              )}
                            </>
                          )}
                        </Panel>
                      </Collapse>
                    </Space>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          ) : (
            <Empty
              description="No integration events found for this order"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </>
      )}
    </Drawer>
  );
}

export default DispatcherDebugPanel;
